import { Button, Kind } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { locationsApi } from 'api/locations'
import { Role } from 'api/roles'
import { RemovableChip } from 'components/RemovableChip'
import Section from 'components/Section'
import { buildTranslate } from 'locales'
import { useMemo, useState } from 'react'
import { RbacLocationPickerModal } from './RbacLocationPickerModal'

const t = buildTranslate('merchant_users.rbac')

const MAX_VISIBLE_CHIPS = 18

type Props = {
  roles: Role[]
  locationIds: number[]
  onChange: (locationIds: number[]) => void
  disabled?: boolean
}

export const RbacLocationsSection = ({
  roles,
  locationIds,
  onChange,
  disabled = false,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { data } = locationsApi.useGetLocationsQuery({ full_visibility: true })
  const allLocations = data?.locations ?? []

  const hasCustomRole = roles.some(r => !r.preset)
  const locationIdSet = useMemo(() => new Set(locationIds), [locationIds])
  if (roles.length === 0 || !hasCustomRole) return null

  const selectedLocations = allLocations.filter(l => locationIdSet.has(l.id))
  const visibleLocations = selectedLocations.slice(0, MAX_VISIBLE_CHIPS)
  const remainingCount = selectedLocations.length - visibleLocations.length

  const handleRemove = (id: number) => {
    onChange(locationIds.filter(x => x !== id))
  }

  const locationLabel = (loc: {
    name: string
    street: string
    city: string
    state: string
  }) => loc.name || `${loc.city}, ${loc.state}`

  return (
    <div className="mt-xl">
      <Text.SmallCaps3 bold className="mb-s">
        {t('locations_header')}
      </Text.SmallCaps3>
      <Section className="p-l">
        <div className="d-flex justify-content-between align-items-center mb-m">
          <div>
            <Text.Header4 bold className="m-0">
              {t('locations_selected')}
            </Text.Header4>
            <Text.BodyText4 color="grey70">
              {selectedLocations.length === 1
                ? t('locations_count', { count: 1 })
                : t('locations_count_plural', {
                    count: selectedLocations.length,
                  })}
            </Text.BodyText4>
          </div>
          <Button
            kind={Kind.SECONDARY}
            size="small"
            onClick={() => setIsModalOpen(true)}
          >
            {disabled ? t('locations_view') : t('locations_edit')}
          </Button>
        </div>

        {selectedLocations.length === 0 ? (
          <Text.BodyText4 color="grey70">{t('locations_none')}</Text.BodyText4>
        ) : (
          <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
            {visibleLocations.map(loc => (
              <RemovableChip
                key={loc.id}
                label={locationLabel(loc)}
                onRemove={() => handleRemove(loc.id)}
                disabledTooltip={disabled ? t('requires_admin') : undefined}
              />
            ))}
            {remainingCount > 0 && (
              <Text.BodyText4 color="grey70" className="align-self-center">
                {t('locations_more', { count: remainingCount })}
              </Text.BodyText4>
            )}
          </div>
        )}
      </Section>

      <RbacLocationPickerModal
        isOpen={isModalOpen}
        initialLocationIds={locationIds}
        onSave={onChange}
        onClose={() => setIsModalOpen(false)}
        disabled={disabled}
      />
    </div>
  )
}
